import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';

const CartSidebar = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    isCartOpen,
    closeCart,
    removeFromCart,
    updateQuantity,
    getCartTotals
  } = useCart();

  const { subtotal, itemCount } = getCartTotals();

  // Prevent body scroll when cart is open
  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCartOpen]);

  // Handle checkout click
  const handleCheckout = () => {
    closeCart();
    navigate('/cart');
  };

  // Get product image
  const getProductImage = (item) => {
    return item.snapshot.imageUrl || item.snapshot.preview_image || null;
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity"
        onClick={closeCart}
      ></div>

      {/* Sidebar */}
      <div className="fixed top-0 right-0 h-full w-full md:w-[400px] bg-gray-900 z-[70] shadow-2xl flex flex-col animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <i className="fas fa-shopping-cart text-chess-green"></i>
            Shopping Cart ({itemCount})
          </h2>
          <button
            onClick={closeCart}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
            aria-label="Close cart"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <i className="fas fa-shopping-cart text-6xl mb-4 opacity-50"></i>
              <p className="text-lg">Your cart is empty</p>
              <button
                onClick={closeCart}
                className="mt-4 px-6 py-2 bg-chess-green text-white rounded-lg hover:bg-chess-green-dark transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            cartItems.map((item) => (
              <div
                key={item.id}
                className="bg-gray-800 rounded-lg p-3 border border-gray-700"
              >
                <div className="flex gap-3">
                  {/* Product Image */}
                  {getProductImage(item) && (
                    <div className="w-20 h-20 flex-shrink-0 bg-gray-900 rounded-lg overflow-hidden">
                      <img
                        src={getProductImage(item)}
                        alt={item.snapshot.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white text-sm line-clamp-2 mb-1">
                      {item.snapshot.title}
                    </h3>

                    {/* Variant Info */}
                    {item.snapshot.variant_title && (
                      <p className="text-xs text-gray-400 mb-1">
                        {item.snapshot.variant_title}
                      </p>
                    )}

                    {/* Size & Color */}
                    {(item.selectedSize || item.selectedColor) && (
                      <p className="text-xs text-gray-400 mb-2">
                        {item.selectedColor && <span>{item.selectedColor}</span>}
                        {item.selectedColor && item.selectedSize && <span> / </span>}
                        {item.selectedSize && <span>{item.selectedSize}</span>}
                      </p>
                    )}

                    {/* Price & Quantity Controls */}
                    <div className="flex items-center justify-between">
                      <span className="text-chess-green font-bold text-sm">
                        ${((item.snapshot.price * item.quantity) / 100).toFixed(2)}
                      </span>

                      <div className="flex items-center gap-1 md:gap-2">
                        {/* Decrease Quantity */}
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded text-white text-sm"
                        >
                          <i className="fas fa-minus"></i>
                        </button>

                        {/* Quantity Display */}
                        <span className="text-white font-semibold text-sm w-5 md:w-6 text-center">
                          {item.quantity}
                        </span>

                        {/* Increase Quantity */}
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded text-white text-sm"
                        >
                          <i className="fas fa-plus"></i>
                        </button>

                        {/* Remove Item */}
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="w-7 h-7 flex items-center justify-center bg-red-900/30 hover:bg-red-900/50 rounded text-red-400 text-sm ml-1 md:ml-2"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer - Sticky Checkout Button */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-700 p-4 space-y-3">
            {/* Subtotal */}
            <div className="flex items-center justify-between text-lg">
              <span className="text-gray-400">Subtotal:</span>
              <span className="text-white font-bold">
                ${(subtotal / 100).toFixed(2)}
              </span>
            </div>

            {/* Shipping Notice */}
            <p className="text-xs text-gray-500 text-center">
              Shipping calculated at checkout
            </p>

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              className="w-full py-3 bg-chess-green hover:bg-chess-green-dark text-white rounded-lg font-semibold transition-all shadow-lg text-base"
            >
              <i className="fas fa-credit-card mr-2"></i>
              Proceed to Checkout
            </button>

            {/* Continue Shopping Link */}
            <button
              onClick={closeCart}
              className="w-full text-sm text-gray-400 hover:text-white transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default CartSidebar;
